import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { Lightbulb, Folder, Description } from '@mui/icons-material';
import { useAuth, vortxColors, retroColors, vortxTypography } from '@vortx/shared-ui';
import { useRetroContext } from '@vortx/shared-ui';
import RetroInputChecklist from './RetroInputChecklist';
import { Skeleton } from '../ux';
import { API_BASE_URL } from '../../services/api';

const DEFAULT_TIPS = [
    "Batteries under 2hr duration rarely pencil for demand charge savings",
    "NEM 3.0 projects benefit from west-facing arrays for afternoon peak",
    "Peak shaving works best with 2-4hr battery duration",
    "Load factor below 40% indicates strong demand charge savings potential",
    "Bifacial modules add 5-10% production for minimal cost increase",
];

const RetroActivityFeed = ({ appConfig, currentProject }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser } = useAuth();
    const { scenarioValidation, setScenarioTab } = useRetroContext();
    const [projects, setProjects] = useState([]);
    const [scenarios, setScenarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tipIndex, setTipIndex] = useState(0);
    const [tipFading, setTipFading] = useState(false);

    const tips = appConfig?.tips || DEFAULT_TIPS;

    // Detect if we're in the scenario editor
    const isInScenarioEditor = location.pathname.includes('/scenarios/');

    // Fetch recent projects
    useEffect(() => {
        const fetchProjects = async () => {
            if (!currentUser) {
                setLoading(false);
                return;
            }
            try {
                const token = await currentUser.getIdToken();
                const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setProjects(data.projects || []);
                }
            } catch (err) {
                console.error('Failed to fetch projects', err);
            } finally {
                setLoading(false);
            }
        };
        fetchProjects();
    }, [currentUser]);

    // Fetch scenarios if we have a current project
    useEffect(() => {
        const fetchScenarios = async () => {
            if (!currentUser || !currentProject?.id) {
                setScenarios([]);
                return;
            }
            try {
                const token = await currentUser.getIdToken();
                const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/${currentProject.id}/scenarios`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setScenarios(data.scenarios || []);
                }
            } catch (err) {
                console.error('Failed to fetch scenarios', err);
            }
        };
        fetchScenarios();
    }, [currentUser, currentProject]);

    // Rotate tips with animation
    useEffect(() => {
        const interval = setInterval(() => {
            setTipFading(true);
            setTimeout(() => {
                setTipIndex(prev => (prev + 1) % tips.length);
                setTipFading(false);
            }, 300);
        }, 8000);

        return () => clearInterval(interval);
    }, [tips.length]);

    // Reset tip index when app changes
    useEffect(() => {
        setTipIndex(0);
    }, [appConfig?.id]);

    const accentColor = appConfig?.color || '#b28c50';

    return (
        <Box sx={{
            background: 'rgba(0, 0, 0, 0.4)',
            border: '1px solid rgba(63, 106, 100, 0.3)',
            padding: 2,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
        }}>
            {/* Context-aware header */}
            {currentProject ? (
                // Show scenarios when viewing a project (shortened to 3 items when in scenario editor)
                <Box sx={{ mb: 2, flexShrink: 0 }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.15em',
                        color: accentColor,
                        marginBottom: 1.5,
                        paddingBottom: 1,
                        borderBottom: `1px solid ${accentColor}40`,
                        transition: 'color 0.3s ease',
                    }}>
                        Project Scenarios
                    </Typography>
                    {scenarios.length === 0 ? (
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.75rem',
                            color: retroColors.dimmed,
                            fontStyle: 'italic',
                        }}>
                            No scenarios yet
                        </Typography>
                    ) : (
                        scenarios.slice(0, isInScenarioEditor ? 3 : 5).map((scenario) => (
                            <Box
                                key={scenario.id}
                                onClick={() => navigate(`/projects/${currentProject.id}/scenarios/${scenario.id}`)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    padding: '0.4rem 0.5rem',
                                    cursor: 'pointer',
                                    borderLeft: '2px solid transparent',
                                    transition: 'all 0.15s ease',
                                    '&:hover': {
                                        borderLeftColor: accentColor,
                                        background: `${accentColor}15`,
                                    }
                                }}
                            >
                                <Description sx={{ fontSize: 14, color: retroColors.muted }} />
                                <Typography sx={{
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.7rem',
                                    color: vortxColors.gray,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}>
                                    {scenario.name}
                                </Typography>
                            </Box>
                        ))
                    )}
                </Box>
            ) : (
                // Show recent projects when on dashboard or list view
                <Box sx={{ mb: 3 }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.15em',
                        color: accentColor,
                        marginBottom: 1.5,
                        paddingBottom: 1,
                        borderBottom: `1px solid ${accentColor}40`,
                        transition: 'color 0.3s ease',
                    }}>
                        Recent Projects
                    </Typography>
                    {loading ? (
                        <Box sx={{ py: 0.5 }}>
                            {[1, 2, 3].map((i) => (
                                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
                                    <Skeleton variant="circular" width={16} height={16} />
                                    <Skeleton variant="text" className="vx-skeleton-text medium" />
                                </Box>
                            ))}
                        </Box>
                    ) : projects.length === 0 ? (
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.75rem',
                            color: retroColors.dimmed,
                            fontStyle: 'italic',
                        }}>
                            No recent projects
                        </Typography>
                    ) : (
                        projects.slice(0, 5).map((project) => (
                            <Box
                                key={project.id}
                                onClick={() => navigate(`/projects/${project.id}`)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    padding: '0.5rem',
                                    cursor: 'pointer',
                                    borderLeft: '2px solid transparent',
                                    transition: 'all 0.15s ease',
                                    '&:hover': {
                                        borderLeftColor: accentColor,
                                        background: `${accentColor}15`,
                                    }
                                }}
                            >
                                <Folder sx={{ fontSize: 14, color: retroColors.muted }} />
                                <Typography sx={{
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.75rem',
                                    color: vortxColors.gray,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}>
                                    {project.name}
                                </Typography>
                            </Box>
                        ))
                    )}
                </Box>
            )}

            {/* Input Checklist - only show when in scenario editor */}
            {isInScenarioEditor && Object.keys(scenarioValidation?.requiredFields || {}).length > 0 && (
                <Box sx={{ flexShrink: 0 }}>
                    <RetroInputChecklist
                        requiredFields={scenarioValidation.requiredFields}
                        missingFields={scenarioValidation.missingFields}
                        onNavigateToTab={setScenarioTab}
                        accentColor={accentColor}
                    />
                </Box>
            )}

            {/* Spacer to push tip to bottom */}
            <Box sx={{ flex: 1, minHeight: 8 }} />

            {/* Contextual Tip - fixed at bottom */}
            <Box sx={{
                flexShrink: 0,
                padding: 1.5,
                background: `${accentColor}15`,
                border: `1px solid ${accentColor}40`,
                transition: 'background 0.3s ease, border-color 0.3s ease',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Lightbulb sx={{ fontSize: 14, color: accentColor, transition: 'color 0.3s ease' }} />
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        color: accentColor,
                        transition: 'color 0.3s ease',
                    }}>
                        Tip
                    </Typography>
                </Box>
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.7rem',
                    color: vortxColors.gray,
                    lineHeight: 1.5,
                    opacity: tipFading ? 0 : 1,
                    transform: tipFading ? 'translateY(-5px)' : 'translateY(0)',
                    transition: 'opacity 0.3s ease, transform 0.3s ease',
                }}>
                    {tips[tipIndex]}
                </Typography>
            </Box>
        </Box>
    );
};

export default RetroActivityFeed;
